import { ai, chatModel } from "../firebase";
import { DOCUMENTATION_SECTIONS } from "../../data/documentationSections";
import { DOCUMENTATION_CONTENT } from "../../data/documentationContent";

const IMAGE_ATTACHMENT_MARKER = "📷 [Image attachment]";

const normalizeText = (text = "") =>
  text
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[\w.-]+@[\w.-]+/g, "")
    .replace(/\b\d{4,}\b/g, "")
    .replace(/[^a-z0-9? ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const sanitizeForStyleContext = (text = "") =>
  text
    .replace(/https?:\/\/\S+/g, "[link]")
    .replace(/[\w.-]+@[\w.-]+/g, "[email]")
    .replace(/\b\d{4,}\b/g, "[number]")
    .trim();

const splitQuestionSentences = (text = "") =>
  text
    .split(/(?<=[?!.])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.endsWith("?"));

const isImageAttachmentMessage = (text = "") =>
  text.includes(IMAGE_ATTACHMENT_MARKER);

const ACTION_LINKS = [
  {
    id: "accounts-load-all",
    label: "Load all transactions",
    route: "/Accounts?chatAction=loadAllTransactions",
    patterns: [
      /load all transactions|show all transactions|missing transactions|transactions?.*(not showing|not appear)|date filter/i,
    ],
  },
  {
    id: "accounts-no-account",
    label: "Show No Account transactions",
    route: "/Accounts?chatAction=showNoAccount",
    patterns: [/no account|unallocated transaction/i],
  },
  {
    id: "pdf-extraction-open",
    label: "Open PDF conversion",
    route: "/ExtractData",
    patterns: [/pdf|extract|convert/i],
  },
  {
    id: "accounts-open",
    label: "Open Accounts",
    route: "/Accounts",
    patterns: [/upload|allocate|transaction|bank statement|account group|cash book/i],
  },
  {
    id: "invoices-open",
    label: "Open Invoices",
    route: "/Invoices",
    patterns: [/invoice|invoicing|quote|quotation|estimate/i],
  },
  {
    id: "company-add",
    label: "Add a company",
    route: "/AddBusiness",
    patterns: [/add (a )?company|add (an )?entity|new business/i],
  },
  {
    id: "software-import-open",
    label: "Open software import",
    route: "/AccountingSoftwareImport",
    patterns: [/quickbooks|migration|software import|general ledger import/i],
  },
  {
    id: "trial-balance-open",
    label: "Open Trial Balance",
    route: "/TBalance",
    patterns: [/trial balance/i],
  },
  {
    id: "ledger-open",
    label: "Open Ledger",
    route: "/Ledger",
    patterns: [/ledger|bank closing balance/i],
  },
  {
    id: "balance-sheet-open",
    label: "Open Balance Sheet",
    route: "/soFP",
    patterns: [/balance sheet|financial position/i],
  },
  {
    id: "vat-open",
    label: "Open VAT Calculation",
    route: "/VatCalculation",
    patterns: [/\bvat\b|vat201/i],
  },
  {
    id: "payroll-open",
    label: "Open Payroll",
    route: "/PayrollDashboard",
    patterns: [/payroll|payslip|employee/i],
  },
  {
    id: "payments-open",
    label: "Open Payments",
    route: "/Payment",
    patterns: [/subscription|pricing|package|upgrade|payment/i],
  },
];

const getActionSuggestions = (text = "") => {
  const seen = new Set();

  return ACTION_LINKS.filter(({ patterns }) =>
    patterns.some((pattern) => pattern.test(text)),
  )
    .filter(({ id, route }) => {
      const key = id || route;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 3)
    .map(({ id, label, route }) => ({ id, label, route }));
};

const tokenizeMessage = (text = "") =>
  normalizeText(text)
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);

const inferConfidence = (responseText = "") => {
  const text = responseText.toLowerCase();

  if (
    /not sure|might be|possibly|cannot confirm|i don't have|unable to|technical difficulties/.test(
      text,
    )
  ) {
    return "low";
  }

  if (/please confirm|check this|if needed/.test(text)) {
    return "medium";
  }

  return "high";
};

// AI-powered chat assistance for FilingStack
class ChatAI {
  constructor() {
    // Check if AI services are available
    this.isAvailable = !!(ai && chatModel);

    try {
      // Use the AI instance and chat-specific model from firebase.js
      this.ai = ai;
      this.model = chatModel;
    } catch (error) {
      console.warn("AI service initialization failed:", error);
      this.ai = null;
      this.model = null;
      this.isAvailable = false;
    }

    // FilingStack-specific context with comprehensive documentation
    this.context = `
    You are a helpful assistant for FilingStack, a professional accounting software.
    Be conversational, friendly, and helpful. Answer user questions naturally and provide guidance.
    
    FILINGSTACK COMPREHENSIVE DOCUMENTATION:
    
    ## Getting Started
    1. SIGNING UP: Clients sign up using email/password or Google account. Users can add additional team members via "Add User" in Navigation menu. Password reset is available on the login page.
    
    2. MY ENTITIES PAGE (Company Management):
       - After login, users see the My Entities page
       - NEW USERS: Get a demo company automatically for free trial
       - ADD NEW COMPANIES/ENTITIES: Click "Add Entity" button on the My Entities page
       - This opens the "Add New Business" form where you enter:
         * Company Name (required)
         * Registration Number
         * Financial Year dates (Start/End)
         * Address, City, Province, PO Box
         * Industry type
         - You can also bulk upload multiple companies via CSV/Excel file
       - Subscription: Month-to-month billing via Payfast (can cancel anytime)
       - Each company can have multiple years added via "Add Year" link
    
    ## Core Features
    3. ACCOUNTS PAGE: Main page for uploading bank statements and allocating transactions. 
       - First step: Add and select a bank account
       - Additional account groups can be added
       - Batch editing available for re-allocation
       - Auto-generates ledger, trial balance, and financial reports
    
    Important: All transactions must be within company's financial year (dates in red = out-of-year transactions) on legacy system.  
    Use "Add Year" link to add different years for the same entity.
    
    Auto-allocation features:
    - Auto Allocate by Prior Allocations: Based on previous similar transaction allocations
    - AI Auto Allocate: Uses Artificial Intelligence for smart transaction categorization
    
    4. UPLOADING BANK STATEMENTS:
       - CSV FORMAT: 3 columns required: Date (YYYY/MM/DD), Description, Amount
       - Positive numbers = receipts/income, Negative = payments/expenses
       - Steps: Choose File → Upload File
       - NO CSV? Use "AI Extract" feature on the "Convert PDF" page to extract data from PDF bank statements
    
    5. ADDING NEW YEAR: Use "Add Year" option to add new financial years for same company. Found on Accounts page.
    
    6. SIMULTANEOUS EDITS: Multiple users can edit simultaneously with automatic conflict resolution.
    
    7. PAGINATION: Navigate large datasets with page controls.
    
    8. TRIAL BALANCE & LEDGER: 
       - Automatically generated based on transaction allocations
       - Shows account balances and detailed transaction history
       - Real-time updates as transactions are allocated
    
    ## Invoicing System
    9. INVOICES: 
       - Create professional invoices with company branding
       - Send via email directly from system
       - Track payment status
       - Generate invoice reports
       - Auto-numbering (INV-1, INV-2, etc.)
       - VAT breakdown on PDF invoices
    
    ## Advanced Features
    10. EDIT PPE NAME: The PPE name can be edited on the Financials >> Balance Sheet page. Edit the PPE description to update it.
    
    11. SOCE (Statement of Comprehensive Income): Detailed income statement generation
    
    12. JOURNALS: 
        - Manual journal entries for adjustments and corrections
        - Double-entry bookkeeping automatically maintained
    
    13. VAT & PROVISIONAL TAX: 
        - Automatic VAT calculations based on transaction allocations
        - Generate VAT returns
        - Calculate provisional tax obligations
    
    14. PAYSLIPS: Generate employee payslips with tax calculations, and UIF declaration filing support
    
    15. FINANCIAL STATEMENTS: 
        - Generate Income Statement, Balance Sheet, Cash Flow Statement automatically
        - Export to PDF
        - Professional formatting for stakeholders
    
    16. ICONS: Visual indicators for different account types and transaction statuses
    
    17. PAYMENT INFORMATION: 
        - Manage subscription on Payments page
        - View usage statistics
        - Update billing details
        - Payroll bundles available (included employees per plan)
    
    18. BANK FEEDS: 
        - Direct bank integration for automatic transaction import
        - Available for Investec and FNB banks
        - Connect/Disconnect functionality
        - Scheduled automatic syncs
    
    19. QUICKSTART GUIDE: Step-by-step setup for new users
    
    20. TUTORIAL VIDEOS: Comprehensive video guides for all features
    
    ## Key FilingStack Benefits:
    - Multi-company support (unlimited companies)
    - Bank reconciliation tools
    - CIPC company registration integration
    - Asset register management
    - Real-time collaboration
    - Cloud-based accessibility (access from anywhere)
    - Professional report generation
    - VAT compliance
    - Payroll processing
    
    ## Common User Tasks & Quick Answers:
    
    HOW TO ADD MORE COMPANIES:
    1. Go to "My Entities" page (accessible from main navigation)
    2. Click "Add Entity" button
    3. Fill in company details form:
       - Company name (required)
       - Registration number
       - Financial year dates
       - Address details
    4. Submit form
    5. New company appears in your My Entities list
    6. Switch between companies using the company selector
    
    Note: You can also bulk upload multiple companies via CSV/Excel file on the Add Entity page.
    
    HOW TO ADD BANK ACCOUNTS:
    1. Go to Accounts page
    2. Click "Add Bank Account"
    3. Enter bank account details
    4. Upload bank statements (CSV format or extract from PDF)
    
    HOW TO ALLOCATE TRANSACTIONS:
    1. Upload bank statement on Accounts page
    2. Select each transaction
    3. Choose account group (Income, Expense, Asset, Liability)
    4. Or use "AI Auto Allocate" for automatic categorization
    
    HOW TO CREATE INVOICES:
    1. Navigate to Invoices section
    2. Click "Create Invoice"
    3. Enter client details and line items
    4. Preview and send via email
    
    HOW TO GENERATE REPORTS:
    1. Go to Financial Statements section
    2. Select report type (Income Statement, Balance Sheet, Cash Flow)
    3. Choose date range
    4. Generate and export to PDF

    ## VERIFIED APP NAVIGATION & WORKFLOW CONTEXT (from live routes/components)
    - Core authenticated areas include: /Accounts, /PdfDisplay, /Invoices, /TaxNavigation, /VatCalculation, /TBalance, /soCI, /soFP, /soCE, /cashflow, /notes, /OpeningBalances, /Businesses, /AddBusiness, /AccountingSoftwareImport, /BulkImport, /MigrationOnboarding, /AuditPortal.
    - Accounts page is the main transaction workspace and supports CSV import/parsing, transaction normalization, categorization, and automation features.
    - PDF/Financial output is handled on /PdfDisplay with statement rendering (SoCI, SoFP, Cash Flow), export options, and report formatting controls.
    - Invoices flow includes /Invoices and /InvoicesPage; invoice import supports structured headers, date normalization, product JSON parsing, and invoice defaults (like generated invoice numbers).
    - Entity/company setup and switching is centered around /Businesses and /AddBusiness, with per-entity selections tied to user context.
    - Data import and migration tooling exists under /BulkImport, /AccountingSoftwareImport, and /MigrationOnboarding.
    - Live chat and support areas are /Support; for screenshots/image uploads in chat, acknowledge receipt and confirm human follow-up.
    - Tax and compliance navigation is surfaced via /TaxNavigation and /VatCalculation, while trial balance and statements are in /TBalance and statement routes.
    - If a user asks "where is" or "how to get to" a feature, prefer route/page-specific guidance using these verified areas first.

    ## MAIN FEATURE PLAYBOOK (verified)
    - Payroll: use /PayrollDashboard for monthly payroll runs, payslip workflows, UIF declaration flows, and payroll month selection. Related pages include /PayrollTotal, /AddEmployee, /leave-management, and /emp201-report.
    - Invoices: use /Invoices and /InvoicesPage for creating and managing invoices; invoice imports support structured headers, date normalization, product JSON parsing, and defaults like generated invoice numbers.
    - Task Manager: /Tasks, /TasksPage, /TasksDetails, /TasksAdded, and /TaskSummary are the primary task flow pages; tasks include status tracking, templates (for VAT, payroll, statements, tax), and client-linked work.
    - Adding Team Members: use /signUpAndAddUser for inviting/adding members and configuring member access; permissions are company-aware and can limit page-level access.
    - Notes: /notes and /NotesMenuPage are used for financial statement notes and note-linked disclosures; notes can run in interactive mode and PDF/report mode.
    - Reports and Financials: /PdfDisplay is the main report rendering/export surface; core financial statement pages include /soCI, /soFP, /soCE, /cashflow, and /TBalance.
    - Entity context matters: most actions depend on selected company and year from UserSelections; when troubleshooting, first confirm the user selected the correct entity/year.

    ## RESPONSE PRIORITY RULES FOR SUPPORT ANSWERS
    - For "where do I do X" questions, answer with exact page names/routes first, then 2-4 concrete steps.
    - For imports (CSV/PDF/accounting software), mention expected format/required fields before troubleshooting deeper errors.
    - For payroll and tax workflows, include the target month/period check as an early validation step.
    - For tasks and collaboration, include status + assignee/client context as key troubleshooting checks.
    - Keep answers practical and app-specific; avoid generic accounting theory unless the user asks for it.

    ## TOP HOW-DO-I INTENTS (route + quick steps)
    1) Add a company/entity -> click Add Entity, complete company details, save, then confirm selected entity/year.
    2) Add a team member -> /signUpAndAddUser -> invite/add user, set member access, confirm company-level permissions.
    3) Upload bank transactions (CSV) -> /Accounts -> select bank account, upload CSV, verify date/amount/description mapping.
    4) Extract transactions from PDF -> /ExtractData (where available in flow) -> run extraction, review rows, then continue allocation.
    5) Import from other accounting software -> /AccountingSoftwareImport -> upload export file, map fields, validate totals before posting.
    6) Bulk import data -> /BulkImport -> choose template/file, validate required columns, import and review errors.
    7) Auto allocate transactions -> /Accounts -> use AI/auto allocation, then review exceptions and uncategorized items.
    8) Add opening balances -> /OpeningBalances -> enter balances per account and save for selected year.
    9) View trial balance -> /TBalance -> confirm entity/year, review account balances and drill into anomalies.
    10) Generate financial statements -> /PdfDisplay with /soCI /soFP /soCE /cashflow data -> review layout then export.
    11) Edit financial statement notes -> /notes or /NotesMenuPage -> open note section, update content, save and recheck PDF/report output.
    12) Create an invoice -> /Invoices or /InvoicesPage -> create invoice, set client/items/tax, save and send.
    13) Import invoices in bulk -> /Invoices (import flow) -> use required headers, validate dates/emails/productsJson, import and verify.
    14) Track invoice payments -> /InvoicesAdded and /InvoiceSummary -> update paid status and reconcile amounts.
    15) Run payroll for a month -> /PayrollDashboard -> select month, review employees, run payroll and payslips.
    16) Add an employee -> /AddEmployee -> capture employee/pay details, save, then confirm appears in payroll month.
    17) Manage leave and statutory payroll items -> /leave-management and /emp201-report -> update leave/statutory values for period.
    18) Create and track tasks -> /Tasks /TasksPage /TasksDetails -> create task, assign status/client, monitor in /TasksAdded and /TaskSummary.
    19) Work on VAT/Tax calculations -> /TaxNavigation and /VatCalculation (plus /ProvTax /ProvTaxCalc when applicable) -> confirm period then calculate.
    20) Get human support in chat -> /Support or Live Chat widget -> share issue summary and screenshot; acknowledge that human support will follow up.

    When user intent matches one of these, answer with the mapped route or ideally the page name first, then provide 2-4 concise action steps.

    ## ADMIN-VERIFIED LIVE CHAT RESPONSE PLAYBOOK (RTDB EXPORT)
    Use these support-verified patterns first when intent matches. Keep answers short, direct, and operational.
    - Add users/client access: "Settings >> User Management". Users can be added there and can log in with their email.
    - Add account groups: "Data Entry >> Accounts". Use the Add Group button on the Account Group column.
    - View account groups: "Data Entry >> Accounts" via the Add button on the Account Group column.
    - Delete wrongly imported transactions/ledger rows: on "Data Entry >> Accounts", click Enable Delete, then Delete All (or delete row-by-row with X).
    - Delete a bank account: use "Data Entry >> Bank Update", select the bank account, then click Delete bank account.
    - Match bank expense to invoice: use the Link button in the first column of the transaction row to select/upload invoice and match.
    - QuickBooks/software migration help: use Data Entry >> Import Software; if file format is unclear, ask user to forward file to info@filingstack.com for review.
    - CSV not reflecting/format issues: advise amount column is numeric in Excel, and request the CSV to info@filingstack.com for verification.
    - Permission/access issue on transactions: confirm account context (single account vs team members), then advise refresh/login cycle.
    - Subscription/per-company pricing confusion: clarify subscription is not charged per company added.
    - Financial statements not updating: advise Auto Generate Financials on Financials >> Trial Balance.
    - Report mismatch checks: request Financials >> Ledger screenshot of bank closing balance and validate date filters.
    - Date visibility issue on Accounts: use Load All Transactions in Filters & Search (also available under + More Options), then search the full range.
    - No Account on Trial Balance: click the No Account row, then use Show all dates for this account in Ledger; use the Accounts action to show No Account rows for allocation.
    - Multiple bank accounts: can be added via Add Bank Account on Data Entry >> Accounts.
    - Audit/duplicate concern: point to Settings >> Audit logs and clarify duplicate prompts can be suggested duplicates.
    - Overdraft display confusion: explain overdraft may appear under liabilities while cash-equivalent asset lines can differ by year sign.
    - For unresolved edge cases, use support-operational wording (review/escalate) rather than long theoretical explanations.
    
    Always be helpful, professional, and conversational.
    Answer questions directly when you know the answer.
    Use the documentation above and the structured UsageInfo documentation to provide accurate information.
    Prefer giving clear, direct answers with minimal follow-up questions. Only ask the user a question if you truly need clarification to avoid giving a wrong answer.
    For very technical issues or account-specific problems, say "Support will review this chat and respond if required" instead of telling users to contact support.
    Keep responses concise but informative (2-4 short sentences for most questions, similar to admin reply length; avoid asking the user what they want next unless absolutely necessary).
    Match the support team's writing style: give the answer first, name the exact menu path, and only add the steps needed to complete the task.
    Prefer one short paragraph (roughly 35-90 words). Use a numbered list only when the user needs to perform several actions.
    Do not invent pages, menu labels, buttons, accounting behavior, or a successful outcome. If the documentation does not verify a detail, say Support will confirm it.
    Use one descriptive markdown navigation link where useful, such as [Data Entry >> Accounts](/Accounts). Never show a bare route beside a page name.
    Avoid headings, filler introductions, repeated conclusions, excessive asterisks, and offers such as "let me know if you need anything else".
    `;

    this.adminUid = "arbkYy2QdNgb5tUMWceoHl3fzCT2";
    this.adminRepliesCache = {
      loadedAt: 0,
      replies: [],
    };
    this.adminRepliesInFlight = null;
    this.adminRepliesConfig = {
      lookbackDays: 365,
      maxChats: 180,
      maxMessagesPerChat: 120,
      maxRepliesInPrompt: 60,
      cacheMs: 15 * 60 * 1000,
    };
  }

  isPlatformAdminUser(uid = "") {
    return !!uid && uid === this.adminUid;
  }

  async getRecentAdminRepliesFromDatabase() {
    const now = Date.now();

    if (
      this.adminRepliesCache.replies.length > 0 &&
      now - this.adminRepliesCache.loadedAt < this.adminRepliesConfig.cacheMs
    ) {
      return this.adminRepliesCache.replies;
    }

    if (this.adminRepliesInFlight) {
      return this.adminRepliesInFlight;
    }

    this.adminRepliesInFlight = (async () => {
      try {
        const [{ auth, database }] = await Promise.all([import("../firebase")]);
        const currentUid = auth?.currentUser?.uid || "";

        // Cross-client query is only allowed for the platform admin account.
        if (!this.isPlatformAdminUser(currentUid)) {
          return [];
        }

        const cutoff =
          now - this.adminRepliesConfig.lookbackDays * 24 * 60 * 60 * 1000;

        const metaSnapshot = await database
          .ref("liveChatMeta")
          .orderByChild("lastMessageTime")
          .startAt(cutoff)
          .limitToLast(this.adminRepliesConfig.maxChats)
          .once("value");

        const chatMeta = metaSnapshot.val() || {};
        const chatIds = Object.keys(chatMeta);

        if (chatIds.length === 0) {
          this.adminRepliesCache = { loadedAt: now, replies: [] };
          return [];
        }

        const messageSnapshots = await Promise.all(
          chatIds.map(async (chatId) => {
            try {
              const snap = await database
                .ref(`liveChat/${chatId}/messages`)
                .orderByChild("timestamp")
                .startAt(cutoff)
                .limitToLast(this.adminRepliesConfig.maxMessagesPerChat)
                .once("value");

              return snap.val() || {};
            } catch (error) {
              console.warn("LiveChat admin-history chat query failed:", {
                chatId,
                message: error?.message,
              });
              return {};
            }
          }),
        );

        const allReplies = messageSnapshots
          .flatMap((messagesObj) =>
            Object.values(messagesObj || {}).map((msg) => ({
              text: (msg?.text || "").toString(),
              timestamp: Number(msg?.timestamp || 0),
              sender: msg?.sender,
            })),
          )
          .filter(
            (msg) =>
              msg.sender === "admin" &&
              msg.text &&
              msg.timestamp >= cutoff &&
              !isImageAttachmentMessage(msg.text),
          )
          .sort((a, b) => b.timestamp - a.timestamp);

        const seen = new Set();
        const uniqueReplies = [];
        for (const reply of allReplies) {
          const key = normalizeText(reply.text);
          if (!key || seen.has(key)) continue;
          seen.add(key);
          uniqueReplies.push(sanitizeForStyleContext(reply.text));

          if (uniqueReplies.length >= this.adminRepliesConfig.maxRepliesInPrompt) {
            break;
          }
        }

        this.adminRepliesCache = {
          loadedAt: now,
          replies: uniqueReplies,
        };

        return uniqueReplies;
      } catch (error) {
        console.warn("LiveChat admin-history load failed:", error?.message || error);
        return [];
      } finally {
        this.adminRepliesInFlight = null;
      }
    })();

    return this.adminRepliesInFlight;
  }

  getIntentTemplate(userMessage = "") {
    const message = userMessage.toLowerCase();

    const templates = [
      {
        match: /add user|add users|user management|client access|give client access|invite user|team member/,
        direct: true,
        response:
          "Settings >> User Management: users can be added there, and they can then log in with their email.",
      },
      {
        match: /upload.*allocate.*transaction|upload and allocate|allocate.*bank transaction/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): select the company, year and bank account, then upload the bank CSV. Allocate No Account rows to the correct account groups, or use prior allocations/AI allocation and review the results before saving.",
      },
      {
        match: /pdf bank statement|convert.*bank statement|bank statement.*pdf/,
        direct: true,
        response:
          "[Data Entry >> Convert PDF](/ExtractData): convert the bank statement, review the extracted dates and amounts, then continue to Accounts to allocate the transactions.",
      },
      {
        match: /upload.*bank statement|where.*upload.*bank|import.*bank statement/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): select the correct company, financial year and bank account, then upload a CSV with Date, Description and Amount columns. Amounts must be numeric; receipts are positive and payments are negative.",
      },
      {
        match: /quickbooks|quick books|data migration|import software|migrate from/,
        direct: true,
        response:
          "[Data Entry >> Import Software](/AccountingSoftwareImport): export banking transactions, journals, opening balances and invoices separately, choose the matching import type, and review the preview totals before posting. Avoid importing the same source file twice.",
      },
      {
        match: /change.*invoice number|edit.*invoice number|starting invoice number|invoice numbering/,
        direct: true,
        response:
          "[Invoicing >> Invoices](/Invoices): open View Invoices and update the Invoice number field before creating the next invoice.",
      },
      {
        match: /match.*invoice|allocate.*invoice|link.*invoice|bank.*invoice/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): use the Link button in the first column of the transaction row to select an invoice or upload an invoice to match that transaction.",
      },
      {
        match: /add.*account group|create.*account group|new account group|insert.*account group/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): use the Add Group button in the Account Group column to create the account group, then allocate the transaction to it.",
      },
      {
        match: /where.*account group|view.*account group|all my account group|show.*account group/,
        direct: true,
        response:
          "Data Entry >> Accounts: you can view them through the Add button on the Account Group column.",
      },
      {
        match: /manual transaction|enter.*transaction manually|capture.*manual transaction/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): use the first row of the transaction table to enter and save a manual bank transaction. Use Data Entry >> Journals instead when the entry did not appear on the bank statement.",
      },
      {
        match: /create.*rule|save.*rule|allocation rule/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): use the Link button in the first column of a transaction row and choose the option to save an allocation rule.",
      },
      {
        match: /batch edit|allocate.*batch|reallocate.*transactions|edit all/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): select the transaction rows, click Edit All, apply the account group or other change, then save. The change applies to all selected rows.",
      },
      {
        match: /export.*transactions|transactions.*excel|prepare csv/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): use Prepare CSV to export the displayed transactions. Load all transactions or adjust the date filters first if the export must include the complete history.",
      },
      {
        match: /delete.*ledger|delete.*import|delete.*bank statement|wrongly uploaded|delete all transactions/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): first click Load All Transactions if the full import must be removed. Then click Enable Delete and Delete All to remove the visible rows, or use X to delete individual rows.",
      },
      {
        match: /delete.*bank account|remove.*bank account/,
        direct: true,
        response:
          "Data Entry >> Bank Update: select the bank account, then click Delete bank account. On Accounts, Delete All only appears after Enable Delete is clicked.",
      },
      {
        match: /no account|unallocated.*trial balance|trial balance.*unallocated/,
        direct: true,
        response:
          "[Financials >> Trial Balance](/TBalance): click the No Account row to open its ledger. In Ledger, use Show all dates for this account; or open Accounts and search for No Account to allocate the affected rows.",
      },
      {
        match: /transactions?.*(not showing|not appear|missing)|uploaded.*(not showing|not reflect)|cash book.*not reflect/,
        direct: true,
        response:
          "[Data Entry >> Accounts](/Accounts): confirm the company, year and bank account, then click Load All Transactions in Filters & Search. If the rows are still missing, check that the CSV Amount column is numeric and that its dates were recognised correctly.",
      },
      {
        match: /subscription.*company|pay.*each company|subscription for each company|do i pay.*company/,
        direct: true,
        response:
          "Settings >> Payments: subscription is not charged per company added.",
      },
      {
        match: /upgrade|choose.*plan|select.*plan|pay.*subscription|subscription.*pay/,
        direct: true,
        response:
          "[Settings >> Payments](/Payment): choose the plan, review the total and active companies, then confirm the subscription. Human support will assist if the displayed total or payment status is incorrect.",
      },
      {
        match: /edit.*chart of accounts|chart of accounts|edit.*account group|can i edit.*account group/,
        direct: true,
        response:
          "Data Entry >> Accounts: account groups are best handled by adding a new group, then using batch edit to move transactions to the new account group.",
      },
      {
        match: /financial statements.*not|balance sheet.*not.*reflect|not generating|auto generate financials/,
        direct: true,
        response:
          "Financials >> Trial Balance: click Auto Generate Financials, then refresh reports.",
      },
      {
        match: /audit log|audit trail|duplicate notice|duplication/,
        direct: true,
        response:
          "Settings >> Audit logs: audit logs are available there, and duplicate notices can be suggested duplicates based on matching date, description, and amount.",
      },
      {
        match: /multiple bank accounts|add.*bank accounts|4 accounts|more than one bank account/,
        direct: true,
        response:
          "Data Entry >> Accounts: use the Add Bank Account link to add multiple bank accounts.",
      },
      {
        match: /opening balance|opening balances/,
        direct: true,
        response:
          "[Data Entry >> Opening Balances](/OpeningBalances): select the company and financial year, enter or import the opening balances, confirm the opening-balance date, and make sure debits and credits balance before saving.",
      },
      {
        match: /quote|quotation|estimate/,
        direct: true,
        response:
          "[Invoicing >> Invoices](/Invoices): click Add Invoice, then select Quote or Estimate as the invoice type and choose the customer or supplier type.",
      },
      {
        match: /business.*dropdown.*(empty|no option|not showing)|company.*not.*invoice/,
        direct: true,
        response:
          "[Invoicing >> Invoices](/Invoices): the Business dropdown only shows businesses added in the invoice dashboard. Add the business first, then return to the invoice and select it.",
      },
      {
        match: /overdraft|cash equivalents.*zero|cash and cash equivalents.*incorrect/,
        direct: true,
        response:
          "Financials >> Ledger: if the bank is in overdraft, the current-year balance can reflect under liabilities. Cash-equivalent lines can differ where prior-year balances were positive assets.",
      },
      {
        match: /tb.*incorrect|trial balance.*incorrect|bank closing balance.*wrong|ledger screenshot/,
        response:
          "Financials >> Ledger: please verify the bank closing balance on the ledger view first, then compare to Trial Balance and statements.",
      },
      {
        match:
          /(rename|edit).*(ppe|property\s*,?\s*plant\s*(and|&)\s*equipment)|(ppe|property\s*,?\s*plant\s*(and|&)\s*equipment).*(rename|edit)/,
        response:
          "The PPE name can be edited on the Financials >> Balance Sheet page. Edit the PPE description to update it.",
      },
      {
        match: /account|bank|transaction|allocate|reconcile/,
        response:
          "[Data Entry >> Accounts](/Accounts)\n1. Select your company and year first.\n2. Upload/import the statement.\n3. Open uncategorized transactions and allocate by group.\n4. Recheck totals before posting.",
      },
      {
        match: /vat|tax return|tax calc|provisional tax/,
        direct: true,
        response:
          "[Tax >> VAT Calculation](/VatCalculation): confirm VAT is enabled in Tax Settings and select the correct period. VAT must be allocated on Accounts or Journals before it appears in the VAT report.",
      },
      {
        match: /invoice|billing client|payment status/,
        direct: true,
        response:
          "[Invoicing >> Invoices](/Invoices): create or open the invoice, confirm the client, line items and VAT, then save and send it. Use the invoice summary to track payment status.",
      },
      {
        match: /payroll|employee|payslip|uif|emp201/,
        direct: true,
        response:
          "[Tax >> Payroll](/PayrollDashboard): select the payroll month, verify employees and statutory settings, then run payroll and generate the payslips. Review EMP/UIF outputs before submission.",
      },
      {
        match:
          /trial balance|tbalance|financial statement|balance sheet|income statement/,
        direct: true,
        response:
          "[Financials >> Trial Balance](/TBalance): confirm the company and year, review No Account and unusual balances, and click an account row to inspect its ledger. Generate or export statements only after those balances are correct.",
      },
    ];

    return templates.find((template) => template.match.test(message)) || null;
  }

  buildEscalationSummary(userMessage, chatHistory, aiDetail) {
    const recentUserMessages = chatHistory
      .filter((msg) => msg.sender === "user" && msg.text)
      .slice(-3)
      .map((msg) => `- ${msg.text}`)
      .join("\n");

    return [
      "Support Summary",
      `Latest issue: ${userMessage}`,
      `AI confidence: ${aiDetail.confidence}`,
      `Needs review: ${aiDetail.needsHumanSupport ? "Yes" : "No"}`,
      aiDetail.response ? `AI draft: ${aiDetail.response}` : "AI draft: n/a",
      recentUserMessages ? `Recent user messages:\n${recentUserMessages}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  getRelevantUsageSections(userMessage = "") {
    const tokens = tokenizeMessage(userMessage);

    if (tokens.length === 0) {
      return DOCUMENTATION_SECTIONS.slice(0, 5);
    }

    const scored = DOCUMENTATION_SECTIONS.map((section) => {
      const keywords = (section.keywords || []).map((kw) => kw.toLowerCase());
      const score = tokens.reduce((total, token) => {
        const hasKeyword = keywords.some(
          (kw) => kw.includes(token) || token.includes(kw),
        );
        return total + (hasKeyword ? 1 : 0);
      }, 0);

      return { section, score };
    })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((entry) => entry.section);

    return scored.length > 0 ? scored : DOCUMENTATION_SECTIONS.slice(0, 5);
  }

  buildUsageInfoContext(userMessage = "") {
    const relevantSections = this.getRelevantUsageSections(userMessage);

    const sectionSummary = relevantSections
      .map(
        (section) =>
          `- ${section.emoji || ""} ${section.title} (keywords: ${(section.keywords || []).join(", ")})`,
      )
      .join("\n");

    const detailedSnippets = relevantSections
      .map((section) => DOCUMENTATION_CONTENT[section.id])
      .filter(Boolean)
      .slice(0, 3)
      .map((doc) => {
        const features = (doc.features || []).slice(0, 4).join("; ");
        return `- ${doc.title}: ${doc.description}${
          features ? ` | Key points: ${features}` : ""
        }`;
      })
      .join("\n");

    return {
      sectionSummary,
      detailedSnippets,
    };
  }

  async generateResponseDetailed(userMessage, chatHistory = []) {
    const intentTemplate = this.getIntentTemplate(userMessage);

    if (isImageAttachmentMessage(userMessage)) {
      const recentText = chatHistory
        .filter((msg) => msg.sender === "user")
        .slice(-4)
        .map((msg) => (msg.text || "").toLowerCase())
        .join(" ");

      let followUp =
        "Please share the exact page and the error text shown in the screenshot.";
      if (/invoice|payment/.test(recentText)) {
        followUp =
          "Please share the invoice number and whether the payment status should be paid or unpaid.";
      } else if (/vat|tax/.test(recentText)) {
        followUp =
          "Please share the VAT period and the specific figure that looks incorrect.";
      } else if (/account|transaction|bank/.test(recentText)) {
        followUp =
          "Please share the bank name and one affected transaction date/amount so support can verify quickly.";
      }

      return {
        response: `Thanks for the screenshot. Support will review this chat and respond if required. ${followUp}`,
        confidence: "medium",
        needsHumanSupport: true,
      };
    }

    const responseText = await this.generateResponse(userMessage, chatHistory, {
      preferredTemplate: intentTemplate?.response || "",
    });
    const confidence = inferConfidence(responseText);
    const needsHumanSupport =
      confidence === "low" || this.needsHumanSupport(userMessage);

    return {
      response:
        needsHumanSupport && confidence === "low"
          ? `${responseText}\n\nSupport will review this chat and respond if required.`
          : responseText,
      confidence,
      needsHumanSupport,
    };
  }

  async generateResponse(userMessage, chatHistory = [], options = {}) {
    const preferredTemplate = options?.preferredTemplate || "";

    // Return fallback response if AI is not available
    if (!this.isAvailable || !this.ai || !this.model) {
      console.log("AI service is not available, returning fallback response");
      if (preferredTemplate) {
        return preferredTemplate;
      }
      return {
        response:
          "I'm currently unable to provide AI-powered responses. Our AI service is temporarily unavailable. Support will review this chat and respond if required.",
        confidence: "low",
        category: "technical",
        needsHumanSupport: true,
      };
    }

    try {
      if (isImageAttachmentMessage(userMessage)) {
        return "Thanks for sending the image. A human support team member will review it and follow up shortly.";
      }

      // Check if AI is properly configured
      if (!this.ai || !this.model) {
        throw new Error("AI service not properly configured");
      }

      // Build conversation context for the chat
      // Include core FilingStack context plus targeted UsageInfo documentation summary
      const usageInfoContext = this.buildUsageInfoContext(userMessage);

      const globalAdminReplies = await this.getRecentAdminRepliesFromDatabase();

      let conversationContext =
        this.context +
        "\n\nUSAGEINFO DOCUMENTATION SUMMARY (titles + keywords):\n" +
        usageInfoContext.sectionSummary +
        "\n\nUSAGEINFO RELEVANT DETAILS:\n" +
        (usageInfoContext.detailedSnippets || "- No extra details matched.") +
        "\n\nRecent conversation:\n";

      // Add more of the recent conversation history for better context
      const MAX_HISTORY_MESSAGES = 20; // previously 5
      const recentMessages = chatHistory.slice(-MAX_HISTORY_MESSAGES);
      recentMessages.forEach((msg) => {
        if (msg.text) {
          conversationContext += `${msg.sender}: ${msg.text}\n`;
        }
      });

      const priorSupportReplies = recentMessages
        .filter((msg) => msg.sender === "admin")
        .map((msg) => sanitizeForStyleContext(msg.text || ""))
        .filter(Boolean)
        .slice(-6);

      if (priorSupportReplies.length > 0) {
        conversationContext +=
          "\nRecent support/admin replies (sanitized, no personal details):\n" +
          priorSupportReplies.map((msg) => `- ${msg}`).join("\n") +
          "\n";
      }

      if (globalAdminReplies.length > 0) {
        conversationContext +=
          "\nAdmin response patterns from all live chats (last 12 months, admin-only DB query):\n" +
          globalAdminReplies.map((msg) => `- ${msg}`).join("\n") +
          "\n";
      }

      if (preferredTemplate) {
        conversationContext +=
          "\nPreferred answer pattern from intent mapping (use when consistent with docs/admin context):\n" +
          `${preferredTemplate}\n`;
      }

      conversationContext += `user: ${userMessage}\n\nPlease provide a helpful, conversational response as a FilingStack support assistant. Answer the user's question directly based on the documentation and UsageInfo summary above. Be friendly but focus on giving a clear solution rather than asking follow-up questions. Only ask the user for clarification if you cannot safely answer without it, and only suggest connecting with human support if the question is about a specific technical issue with their account, billing problems, or bugs/errors.

Additional response rules:
- Always review recent admin replies and UsageInfo documentation context before drafting your final response.
- Prioritize the Admin-Verified Live Chat Response Playbook over generic explanations when there is a direct intent match.
- Keep wording close to support-agent style used in prior admin replies; avoid introducing alternate workflows unless required.
- Aim for 35-90 words and one compact paragraph. Use 2-4 numbered steps only when the user must complete a sequence.
- Answer only the latest unresolved question. Do not recap the whole conversation or repeat an earlier AI answer.
- Do not ask the same question that support or AI already asked earlier in this chat.
    - If prior support replies already requested details, do not request them again unless there is a materially different missing detail.
    - When an action button is available, describe the same action in the response so the user understands what it will do before clicking it.
- Use prior admin replies as tone/context guidance, but do not repeat personal information, email addresses, phone numbers, links, or account-specific identifiers.
- If the user sent an image attachment, thank them and state that a human support team member will review it and follow up shortly. Do not say you cannot process image attachments.
- Prefer statements over questions unless a new question is strictly necessary to move the issue forward.
- Start with a verified full page reference when relevant, using this format: [Parent Area >> Page](/route).
- Include at most two links. Use descriptive labels and never display a bare route such as "Accounts (/Accounts)".
- Never invent a route or menu label. If the exact location is not present in the supplied documentation, omit the link and say Support will confirm the location.
- For large or complex explanations, especially debit/credit guidance, use a compact markdown table.
- Do not give definitive accounting or tax treatment for an account-specific scenario; provide the FilingStack workflow and flag the treatment for professional/support confirmation.
- Keep formatting clean with minimal asterisks and avoid decorative markdown, headings, and closing offers.`;

      // Use the chat-specific model configured in firebase.js
      const result = await this.model.generateContent(conversationContext);
      const response = result.response;

      // Parse the structured JSON response from the chat model
      let responseData;
      try {
        responseData = JSON.parse(response.text());
      } catch {
        // Fallback if JSON parsing fails - use raw response and filter it
        let rawResponse = response.text();

        // Filter out negative responses and replace with helpful alternatives
        rawResponse = this.filterNegativeResponses(rawResponse);

        return rawResponse || "Human support will assist with this.";
      }

      // Extract the response text and additional metadata
      let responseText =
        responseData.response || "Human support will assist with this.";

      // Filter negative responses
      responseText = this.filterNegativeResponses(responseText);
      responseText = this.removeRepeatedQuestions(responseText, chatHistory);

      // Optional: Use additional metadata for logging or escalation
      if (responseData.needsHumanSupport) {
        console.log("AI suggests human support for this query");
      }
      if (responseData.confidence === "low") {
        console.log("AI has low confidence in this response");
      }

    return responseText;
    } catch (error) {
      console.error("AI response error:", error);

      // Return a helpful fallback response
      if (error.message.includes("PERMISSION_DENIED")) {
        return "I'm currently unable to access the AI service. However, I can still help with basic FilingStack questions. Support will review this chat and respond if required.";
      }

      return "I'm having trouble processing your request right now. Support will review this chat and respond if required.";
    }
  }

  // Filter out negative responses and replace with positive alternatives
  filterNegativeResponses(responseText) {
    const negativePatterns = [
      /i don't have information about/gi,
      /this is beyond my capabilities/gi,
      /unfortunately.*not possible/gi,
      /i cannot process image attachments/gi,
    ];

    let filteredResponse = responseText;

    negativePatterns.forEach((pattern) => {
      if (pattern.test(filteredResponse)) {
        if (/image attachments/gi.test(filteredResponse)) {
          filteredResponse =
            "Thanks for sending the image. A human support team member will review it and follow up shortly.";
        } else {
          filteredResponse =
            "Support will review this chat and respond if required.";
        }
      }
    });

    return filteredResponse;
  }

  removeRepeatedQuestions(responseText, chatHistory = []) {
    const priorQuestions = new Set(
      chatHistory
        .filter((msg) => msg.sender === "admin")
        .flatMap((msg) => splitQuestionSentences(msg.text || ""))
        .map((sentence) => normalizeText(sentence))
        .filter(Boolean),
    );

    if (priorQuestions.size === 0) {
      return responseText;
    }

    const sentences = responseText
      .split(/(?<=[?!.])\s+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    const filteredSentences = sentences.filter((sentence) => {
      if (!sentence.endsWith("?")) {
        return true;
      }

      return !priorQuestions.has(normalizeText(sentence));
    });

    if (filteredSentences.length === sentences.length) {
      return responseText;
    }

    const rebuilt = filteredSentences.join(" ").trim();
    if (rebuilt) {
      return rebuilt;
    }

    return "Thanks for the update. A human support team member will review the conversation and follow up shortly.";
  }

  // Quick AI responses for common FilingStack questions
  async getQuickResponse(userMessage) {
    if (isImageAttachmentMessage(userMessage)) {
      return "Thanks for sending the image. A human support team member will review it and follow up shortly.";
    }

    const template = this.getIntentTemplate(userMessage);
    return template?.direct ? template.response : null;
  }

  // Check if message seems like it needs human support
  needsHumanSupport(userMessage) {
    const message = userMessage.toLowerCase();

    const humanSupportKeywords = [
      "speak to human",
      "human agent",
      "real person",
      "billing issue",
      "payment problem",
      "refund",
      "bug",
      "error",
      "broken",
      "not working",
      "urgent",
      "emergency",
      "asap",
      "complaint",
      "frustrated",
      "angry",
    ];

    return humanSupportKeywords.some((keyword) => message.includes(keyword));
  }

  // Auto-responses based on admin availability
  getAutoResponse(adminOnline, userMessage) {
    if (this.needsHumanSupport(userMessage)) {
      if (adminOnline) {
        return "I understand you'd like a human agent. Support will review this chat and respond shortly.";
      } else {
        return "I see you need human assistance. Support will review this chat and respond if required. In the meantime, I'm happy to help with FilingStack questions.";
      }
    }

    // Only return auto-response for specific greeting scenarios, not all offline cases
    return null; // Let AI handle most cases when admin is offline
  }
}

// Usage example for integration:
export const chatAI = new ChatAI();

// Function to add to ChatWidget.js
export const handleAIResponse = async (
  userMessage,
  chatHistory,
  adminOnline,
  chatId,
) => {
  try {
    // Check for auto-response first
    const autoResponse = chatAI.getAutoResponse(adminOnline, userMessage);
    if (autoResponse) {
      await sendAIMessage(autoResponse, chatId, {
        adminOnline,
      });
      return {
        response: autoResponse,
        confidence: "medium",
        needsHumanSupport: true,
      };
    }

    // Try quick response
    const quickResponse = await chatAI.getQuickResponse(userMessage);
    if (quickResponse) {
      await sendAIMessage(quickResponse, chatId, {
        adminOnline,
        actionSuggestions: getActionSuggestions(
          `${userMessage} ${quickResponse}`,
        ),
      });
      return {
        response: quickResponse,
        confidence: "high",
        needsHumanSupport: false,
      };
    }

    // If admin is offline and no quick response, generate AI response
    if (!adminOnline) {
      const aiDetail = await chatAI.generateResponseDetailed(
        userMessage,
        chatHistory,
      );

      await sendAIMessage(aiDetail.response, chatId, {
        aiConfidence: aiDetail.confidence,
        needsHumanSupport: aiDetail.needsHumanSupport,
        adminOnline,
        actionSuggestions: getActionSuggestions(`${userMessage} ${aiDetail.response}`),
      });

      if (aiDetail.needsHumanSupport) {
        const escalationSummary = chatAI.buildEscalationSummary(
          userMessage,
          chatHistory,
          aiDetail,
        );
        const [{ writeLiveChatInfo }] = await Promise.all([
          import("./liveChatMeta"),
        ]);

        await writeLiveChatInfo(chatId, {
          status: "reviewing",
          needsHumanReview: true,
          escalationSummary,
          escalationAt: Date.now(),
          aiConfidence: aiDetail.confidence,
        });
      }

      return aiDetail;
    }

    return null;
  } catch (error) {
    console.error("AI handling error:", error);

    // Fallback response when AI fails
    const fallbackMessage =
      "I'm having some technical difficulties right now. " +
      (adminOnline
        ? "Support will assist you shortly."
        : "Support will review this chat and respond if required.");

    await sendAIMessage(fallbackMessage, chatId, {
      aiConfidence: "low",
      needsHumanSupport: true,
      adminOnline,
    });

    return {
      response: fallbackMessage,
      confidence: "low",
      needsHumanSupport: true,
    };
  }
};

// Helper function to send AI message
const sendAIMessage = async (message, chatId, meta = {}) => {
  const [{ database }, { writeLiveChatInfo }] = await Promise.all([
    import("../firebase"),
    import("./liveChatMeta"),
  ]);

  const aiMessage = {
    text: message,
    sender: "ai",
    timestamp: Date.now(),
    senderName: "FilingStack AI Assistant",
    read: false,
    aiConfidence: meta.aiConfidence || "medium",
    needsHumanSupport: !!meta.needsHumanSupport,
  };

  await database.ref(`liveChat/${chatId}/messages`).push(aiMessage);

  // Update chat info and lightweight summary
  const nextStatus = meta.needsHumanSupport
    ? "reviewing"
    : meta.adminOnline
      ? "in_progress"
      : "awaiting_user";

  await writeLiveChatInfo(chatId, {
    lastMessage: message,
    lastMessageTime: Date.now(),
    status: nextStatus,
    aiConfidence: meta.aiConfidence || "medium",
    needsHumanReview: !!meta.needsHumanSupport,
    ...(meta.actionSuggestions?.length
      ? { actionSuggestions: meta.actionSuggestions }
      : {}),
  });
};

export default ChatAI;
